import {
  assertIsEntityTypeForAccessDetails,
  DataSetId,
  SingleDataSetAccessDetails,
} from '@upright/common/iam/access/access-details/types';
import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { pickDefaultModelResult } from '@upright/common/model/data-point-pickers';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { RenderItem } from '@upright/common/renderer/types';
import { reactQueryToLegacyApiData } from '@upright/ui/api/common/util';
import { useGetAccessDetails } from '@upright/ui/api/iam/access/hooks';
import { useGetModelEntityScoreData } from '@upright/ui/api/model/output/scores/model-entity-data/use-get-model-entity-score-data';
import { useWeightSetItemResults } from '@upright/ui/api/model/output/scores/weight-sets';
import { useHasAccessToFeature } from '@upright/ui/common/generic/hooks';
import {
  LensDataAccess,
  LensDataAvailability,
  SingleEntityLensData,
} from '@upright/ui/common/upright/core/impact-lens-profile/types';
import { NetImpactLensData } from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { getDefaultRelease } from '@upright/ui/untidy/redux/selectors';
import { useTopImpactDatas } from '@upright/ui/views/model-entity-views/groupfolio-view/use-top-impact-data';
import { keyBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useRenderViewContext } from '../../../context';

export function useGetRenderViewNetImpactLensData(
  item: RenderItem,
): SingleEntityLensData<ImpactLensType.UPRIGHT_NET_IMPACT> {
  const context = useRenderViewContext();
  const defaultRelease = useSelector(getDefaultRelease);
  const release = context.options.release ?? defaultRelease;

  assertIsEntityTypeForAccessDetails(item.type);

  const query = useGetModelEntityScoreData({
    modelEntityReference: { id: item.id, type: item.type },
    overrides: { releaseOrVersion: release },
  });

  const entity = query.data;
  const modelResultId = item.resultId;
  const selectedModelResult = modelResultId ? entity?.modelResults.find(d => d.resultId === modelResultId) : undefined;
  const defaultModelResult = entity?.modelResults ? pickDefaultModelResult(entity.modelResults) : undefined;
  const modelResult = selectedModelResult ?? defaultModelResult;

  if (modelResultId && entity && !modelResult) {
    throw Error(`Model result with id ${modelResultId} not found`);
  }

  const weightSetId = modelResult?.weightSetId;

  const itemDataQuery = useWeightSetItemResults(weightSetId, release, {
    includeEuTaxonomyMetrics: false,
    includePaiMetrics: false,
  });
  const weightSetItems = itemDataQuery.data;
  const weightSetItemsById = weightSetItems ? keyBy(weightSetItems, i => i.id) : undefined;

  const legacyScoreDataRequest = reactQueryToLegacyApiData(query);

  const accessDetailsQuery = useGetAccessDetails({
    entityId: item.id,
    entityType: item.type,
    release: modelResult?.release,
  });

  const accessDetails = accessDetailsQuery.data?.details;

  const lensAccessDetails = accessDetails ? accessDetails[DataSetId.netImpact] : undefined;
  const lensAccessibility = getLensDataAccessibility(lensAccessDetails);
  const lensDataAvailability = getLensDataAvailability(lensAccessDetails);

  const lensData: NetImpactLensData = {
    type: ImpactLensType.UPRIGHT_NET_IMPACT,
    takeaways: modelResult?.keyTakeaways,
    modelResultId: modelResult?.resultId,
    revenue: modelResult?.revenue,
    coverageClass: modelResult?.coverageClass,
    productNames: modelResult?.productNames,
    scores: modelResult?.impactScoresByTIVV[FinalImpactScoreType.RELATIVE],
    entity,
    loadingStatus: legacyScoreDataRequest.requestStatus,
    access: lensAccessibility,
    availability: lensDataAvailability,
    hasProductViewAccess: useHasAccessToFeature(FeatureId.PRODUCT_VIEW),
    version: modelResult?.version,
    accessLevel: entity?.accessLevel,
    groupType: entity?.groupType,
    weightSetItemsById,
    isLoadingWeightSetItems: itemDataQuery.status === 'pending',
    topImpactData: useTopImpactDatas(entity?.type, modelResult).data,
  };

  return lensData;
}

const getLensDataAccessibility = (accessDetails: SingleDataSetAccessDetails | undefined) => {
  if (!accessDetails) {
    return LensDataAccess.UNKNOWN;
  }

  return accessDetails.isAccessibleToUser ? LensDataAccess.GRANTED : LensDataAccess.DENIED;
};

const getLensDataAvailability = (accessDetails: SingleDataSetAccessDetails | undefined) => {
  if (!accessDetails || !accessDetails.isAccessibleToUser) {
    return LensDataAvailability.UNKNOWN;
  }

  return accessDetails.isDataAvailable ? LensDataAvailability.AVAILABLE : LensDataAvailability.UNAVAILABLE;
};
